import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Shared with the board's card so one container reads the same on both screens:
// the same dash for an absent value, the same date shape, and the same rule
// about never showing a date where a time belongs.
import { EMPTY, onSiteSlot } from "./PlanningCardView";
import {
  CalendarClock,
  ChevronDown,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  ChevronRight,
  Crosshair,
  Loader2,
  RotateCcw,
  Search,
  Truck,
  X,
} from "lucide-react";

import { loadGoogleMapsLibrary } from "../works/useGooglePlacesSuggestions";
import { planningAPI } from "../../services/api";
import { useSocketEvent } from "../../hooks/useSocketEvent";
import type {
  PlanningMapContainer,
  PlanningMapStop,
} from "../../types/planning";

/**
 * ── The whole week's work, on one map ────────────────────────────────────────
 *
 * Every container the board could involve — all of the waiting ones and the
 * placed ones whose slots fall in the window — drawn at the point it is
 * collected from and labelled with the number the dispatcher calls it by.
 *
 * ── Why this is not the card's map ───────────────────────────────────────────
 *
 * `PlanningCardMap` answers "whereabouts is this one job", for one container,
 * as the stops of its run. This answers a different question entirely: *which
 * of these jobs are near each other*. That is the question that decides which
 * boxes should share a truck and what order a truck's day should run in, and it
 * cannot be asked one card at a time — a dispatcher flicking through two
 * hundred cards to build a mental map of the week is doing by memory the one
 * thing a map is for.
 *
 * So each container is one point here, not a route. Drawing three stops and a
 * road for every container would put six hundred marks on the canvas to answer
 * a question about a hundred, and the roads — all of them running back to the
 * same two terminals — would cover the clustering that is the entire signal.
 *
 * ── Why it fetches rather than reading the board ─────────────────────────────
 *
 * It used to draw the board's own cards, and that was wrong in a way that only
 * showed at scale: the board's waiting pool is a capped page, so on a company
 * with three thousand unplanned containers the map drew the two hundred nearest
 * the front of a queue and said nothing about it. The front two hundred of a
 * queue are not a sample of anything *geographic*, which makes a map built from
 * them worse than no map — it invites exactly the conclusion it cannot support.
 *
 * `/planning/map` answers instead, with a row about a third of the size and no
 * cap on the waiting half (see `getMap`). The board's own payload got lighter
 * in the trade: it no longer carries coordinates at all, because nothing reads
 * them there.
 *
 * ── Pins that survive a real week ────────────────────────────────────────────
 *
 * Containers do not spread themselves out politely. Six boxes off one client's
 * yard share a loading point *exactly*, and no amount of zooming will ever
 * separate points that are genuinely identical. So overlapping pins are fanned
 * apart in screen pixels — each keeping a leader line back to the point it
 * really sits on — which is the same treatment, for the same reason, that the
 * route map gives a container collected from and returned to one terminal.
 *
 * Only a crowd too big to fan stays a counted disc, and clicking one lists what
 * is inside it. A group that is merely near, rather than identical, splits by
 * itself as the zoom goes in.
 */

/** What a pin is saying about its container, and the colour that says it. */
type PinTone = "waiting" | "planned" | "sent";

/**
 * Three states, because they are the three answers to "what is left to do".
 *
 * Copper is in no slot — the pile the dispatcher is here to distribute, whether
 * or not a truck has been named on it. Ink is in a slot but still the office's
 * own business. Green is on a driver's screen and effectively spent. Reading a
 * county's worth of copper in one corner is the whole reason this screen exists.
 */
const TONE_COLOR: Record<PinTone, string> = {
  waiting: "#C97A3C", // copper
  planned: "#0B0F0E", // ink
  sent: "#00A862", // forest
};

const toneOf = (container: PlanningMapContainer): PinTone => {
  if (!container.cells.length) return "waiting";
  return container.sent ? "sent" : "planned";
};

/**
 * The teardrop a container wears — the same shape the container's own route map
 * uses, so a pin means the same thing on both screens.
 *
 * Hollow rather than numbered: on a route the number is the stop's place in the
 * run, and here there is no run to be in. The hole reads as a map pin at a
 * glance and keeps the colour — which is the container's state — on the rim
 * where it survives being drawn small.
 */
const pinSvg = (color: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="40" viewBox="0 0 30 40">
     <path d="M15 39C15 39 28 23.8 28 14.5A13 13 0 1 0 2 14.5C2 23.8 15 39 15 39Z"
           fill="${color}" stroke="#FFFFFF" stroke-width="2.5"/>
     <circle cx="15" cy="14" r="4.6" fill="#FFFFFF" fill-opacity="0.92"/>
   </svg>`;

interface MegaPin {
  id: string;
  lat: number;
  lng: number;
  /** The container's number — the first line of the label. */
  label: string;
  /**
   * The truck it is already on, or null while it is on none.
   *
   * The truck and nothing else. Naming the slots here as well — which this used
   * to do, as `123 ABC · Mon/2, Tue/1` — put a container's whole timetable on a
   * pin: the labels grew wider than the places they pointed at, which meant more
   * of them collided, which meant more of them were dropped. A label's job is to
   * say *which container this is*; the timetable belongs on the card, where
   * there is room to lay it out properly.
   */
  plate: string | null;
  /**
   * How many slots it holds — not which ones.
   *
   * Drawn as a small count on the pin rather than spelled out beside it, so the
   * map still answers "is this one planned, and how much of a truck's week does
   * it take" at a glance, and clicking it answers "which days".
   */
  slots: number;
  tone: PinTone;
  /** The tooltip — the label is short by design, this is not. */
  title: string;
}

interface MegaPinsHandle extends google.maps.OverlayView {
  /** Re-style for a new selection without rebuilding the overlay. */
  highlight(ids: string[]): void;
}

type MegaPinsCtor = new (
  pins: MegaPin[],
  options: { onSelect: (ids: string[]) => void },
) => MegaPinsHandle;

let MegaPins: MegaPinsCtor | null = null;

const megaPinsClass = (maps: typeof google.maps): MegaPinsCtor => {
  if (MegaPins) return MegaPins;

  /** Pins projecting closer than this are in each other's way. */
  const CLUSTER_PX = 30;

  /**
   * The biggest crowd still worth fanning out.
   *
   * Past this the ring is wider than the thing it describes and the labels on
   * it collide with each other anyway, so a counted disc says more — and
   * clicking it lists every container in the panel, which is a better reading of
   * twenty boxes than twenty overlapping pills would be.
   */
  const FAN_MAX = 8;

  /** Adjacent pins on a fan sit at least this far apart, centre to centre. */
  const SPACING_PX = 40;

  /**
   * The room one label needs on a fan, top to bottom.
   *
   * A fan is sized against this rather than against the pins, and that is the
   * whole lesson of the first version: pins spaced just far enough not to touch
   * still had labels sitting on top of each other, so the fitting pass dropped
   * four of five and the fan read as one container with four anonymous pins
   * beside it. Separating the pins was never the point — separating the *names*
   * is, because the name is what a dispatcher came to read.
   */
  const LABEL_ROW = 26;

  /**
   * How far a fan may move a pin from where the container actually is, on the
   * ground, in metres.
   *
   * ── Why a fan needs a ground budget at all ──────────────────────────────────
   *
   * A fan is a screen-space device: it pushes pins apart by pixels so that
   * points which are genuinely identical can still be told apart. That is
   * honest only while those pixels are a short distance on the *ground* — and
   * whether they are depends entirely on the zoom.
   *
   * Zoomed out to the continent, one pixel is a kilometre and a half. A fan of
   * eight around a yard in Rust then puts pins in Belgium, France and Italy: the
   * leader lines are technically there, but the first thing a reader takes off
   * that map is "these containers are spread across Europe", which is the exact
   * opposite of what the fan was drawn to say. A map that has to be studied
   * before it stops misleading you is not doing its job.
   *
   * So a fan is only drawn when its radius is worth less than this on the
   * ground — roughly the size of an industrial estate, which is what "the same
   * place" means to a dispatcher. Above it the group stays a counted disc,
   * which says exactly what is true at that zoom: several containers, here.
   * Zooming in buys the budget back and the fan opens by itself.
   */
  const FAN_GROUND_M = 750;

  /**
   * How far apart two containers may be and still be *one place*, in metres.
   *
   * ── The distinction a fan depends on ────────────────────────────────────────
   *
   * A fan says something specific: "every one of these is at this point". It
   * draws each pin on a ring with a leader back to a single shared anchor, and
   * that is only true when they really do share the anchor.
   *
   * Pixel overlap is not that. Two containers three hundred metres apart overlap
   * at one zoom and separate at the next, and fanning them asserts a shared
   * point they do not have — worse, it *moves* each pin off the place it
   * belongs, so a container loading at the chalet up the road gets drawn in the
   * middle of the park with a hairline as the only hint. A map that puts a pin
   * somewhere the container is not has failed at the one thing it does.
   *
   * So the two crowds are told apart by what they are rather than by how they
   * happen to land: containers that genuinely share a site — the same Places
   * lookup, the same yard gate, coordinates identical or within a few dozen
   * metres — get the fan, because no zoom will ever separate them. Containers
   * that are merely near each other get a counted disc, because zooming *will*
   * separate them, honestly, into the different places they are.
   */
  const SAME_PLACE_M = 50;

  /** Metres per pixel on a Web Mercator tile at this zoom and latitude. */
  const groundScale = (zoom: number, lat: number) =>
    (156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom;

  /** The teardrop's box. It is anchored at its tip, so it hangs above its point. */
  const PIN_W = 30;
  const PIN_H = 40;

  /**
   * How far the label sits from the pin's near edge, and how high up it.
   *
   * `LABEL_GAP` is measured from whichever side the label is on — a fanned pin
   * on the left of its circle wears its label to the *left*, or every label on
   * that side would be drawn straight across the pins in the middle.
   */
  const LABEL_GAP = 36;
  const LABEL_MID = 14;

  const SVG_NS = "http://www.w3.org/2000/svg";

  MegaPins = class Pins extends maps.OverlayView {
    private root = document.createElement("div");
    private leaders = document.createElementNS(SVG_NS, "svg");
    private nodes: HTMLElement[] = [];
    private labels: HTMLElement[] = [];
    private lines: SVGLineElement[] = [];
    private labelWidths: number[] = [];
    private labelHeights: number[] = [];
    private points: google.maps.LatLng[] = [];

    /** Discs are reused across draws — a pan must not churn the DOM. */
    private discs: HTMLElement[] = [];
    private discInners: HTMLElement[] = [];
    private discPool = 0;

    private selected = new Set<string>();

    constructor(
      private pins: MegaPin[],
      private options: { onSelect: (ids: string[]) => void },
    ) {
      super();
    }

    onAdd() {
      this.root.style.cssText =
        "position:absolute;left:0;top:0;width:0;height:0";
      this.leaders.setAttribute(
        "style",
        "position:absolute;left:0;top:0;width:1px;height:1px;overflow:visible;pointer-events:none",
      );
      // Added before the pins so a leader always passes beneath them.
      this.root.appendChild(this.leaders);

      this.pins.forEach((pin, i) => {
        this.points.push(new maps.LatLng({ lat: pin.lat, lng: pin.lng }));

        const line = document.createElementNS(SVG_NS, "line");
        line.setAttribute("stroke", "#0B0F0E");
        line.setAttribute("stroke-width", "1.5");
        line.setAttribute("stroke-opacity", "0.45");
        line.style.display = "none";
        this.leaders.appendChild(line);
        this.lines.push(line);

        const node = document.createElement("div");
        node.style.cssText = [
          "position:absolute",
          `width:${PIN_W}px;height:${PIN_H}px`,
          // Anchored at the tip, and scaled from it, so a pin grows upwards
          // rather than sliding off the place it is pointing at.
          "transform:translate(-50%,-100%)",
          "transform-origin:50% 100%",
          "cursor:pointer",
          `z-index:${10 + i}`,
        ].join(";");
        node.title = pin.title;

        const head = document.createElement("div");
        head.style.cssText = "position:absolute;inset:0";
        head.innerHTML = pinSvg(TONE_COLOR[pin.tone]);
        node.appendChild(head);

        /*
         * The container's id, and the truck after it.
         *
         * One line, separated by a dash: two stacked lines were twice as tall,
         * and height is what a fan has least of — a taller label means a wider
         * fan to keep them apart, which spreads a yard's containers further
         * across the map than the yard deserves.
         */
        const label = document.createElement("span");
        label.style.cssText = [
          // `left`/`right` are set per draw — see `place`.
          `position:absolute;top:${LABEL_MID}px`,
          "transform:translateY(-50%)",
          "pointer-events:none;white-space:nowrap;border-radius:8px",
          "font-family:inherit",
          "padding:2px 7px",
          "background:#FFFFFF",
          "box-shadow:0 1px 3px rgba(11,15,14,0.22)",
          "display:flex;align-items:baseline;gap:3px;line-height:1.3",
        ].join(";");

        const name = document.createElement("span");
        name.textContent = pin.label;
        name.style.cssText = "font-weight:700;font-size:10.5px;color:#0B0F0E";
        label.appendChild(name);

        if (pin.plate) {
          const plate = document.createElement("span");
          /*
           * A middot, not a dash.
           *
           * The id already contains a hyphen (`CO003-02`), so a dash between id
           * and truck put two dashes doing two different jobs in one short
           * string — `CO003-02 - BUS-156` reads as one hyphenated thing before
           * it reads as two. The dot cannot be mistaken for part of either.
           */
          plate.textContent = `· ${pin.plate}`;
          plate.style.cssText =
            "font-weight:600;font-size:9.5px;color:#586762;letter-spacing:0.02em";
          label.appendChild(plate);
        }

        node.appendChild(label);

        /*
         * How many slots it holds, on the pin itself.
         *
         * The count and not the days: "this takes three of a truck's slots" is
         * a fact a dispatcher can act on from across the map, and *which* three
         * is a question with an answer too long for a pin. So the badge says
         * there is something to open, and the card says what.
         */
        if (pin.slots > 0) {
          const badge = document.createElement("span");
          badge.textContent = String(pin.slots);
          badge.style.cssText = [
            "position:absolute;right:-3px;top:-3px",
            "display:flex;align-items:center;justify-content:center",
            "min-width:15px;height:15px;padding:0 3px;border-radius:9999px",
            "background:#FFFFFF",
            `color:${TONE_COLOR[pin.tone]}`,
            `border:1.5px solid ${TONE_COLOR[pin.tone]}`,
            "font-family:inherit;font-weight:800;font-size:9px;line-height:1",
            "box-shadow:0 1px 2px rgba(11,15,14,0.25)",
          ].join(";");
          node.appendChild(badge);
        }

        // The whole pin answers the click, label included — a teardrop's tip is
        // a hard target on a tablet and the label is the part people aim at.
        node.addEventListener("click", (event) => {
          event.stopPropagation();
          this.options.onSelect([pin.id]);
        });
        // Hovering lifts a pin over its neighbours, which is how a label that
        // was dropped for want of room gets read without zooming.
        node.addEventListener("pointerenter", () => {
          node.style.zIndex = "9000";
          label.style.display = "";
        });
        node.addEventListener("pointerleave", () => {
          node.style.zIndex = String(10 + i);
          if (node.dataset.labelled === "0") label.style.display = "none";
        });

        this.root.appendChild(node);
        this.nodes.push(node);
        this.labels.push(label);
      });

      this.getPanes()?.floatPane.appendChild(this.root);

      /**
       * Every label measured once, while they are all still on screen.
       *
       * Their size decides which of them fit, and a label hidden by an earlier
       * draw measures zero — so asking later would let one dropped label keep
       * itself dropped for ever. One forced layout at mount, and none after.
       * Both axes, because a label is two lines tall where the container has a
       * truck and one where it has not.
       */
      this.labelWidths = this.labels.map((label) => label.offsetWidth);
      this.labelHeights = this.labels.map((label) => label.offsetHeight);
    }

    highlight(ids: string[]) {
      this.selected = new Set(ids);
      this.draw();
    }

    draw() {
      const projection = this.getProjection();
      const map = this.getMap() as google.maps.Map | null;
      if (!projection || !map) return;

      /**
       * Only what is on screen is laid out at all.
       *
       * The map can hold a company's whole backlog, and a dispatcher zoomed into
       * one city is asking about that city. Skipping the rest keeps the
       * grouping, the fanning and the label fitting proportional to what is
       * visible rather than to what exists, which is what lets three thousand
       * containers stay smooth to pan on a tablet.
       */
      const bounds = map.getBounds();

      /*
       * What a pixel is worth on the ground right now — the thing that decides
       * whether a fan is a clarification or a lie. Taken at the map's centre
       * latitude, which is close enough: the scale only varies with latitude,
       * and a screen does not span enough of it to matter.
       */
      const metresPerPixel = groundScale(
        map.getZoom() ?? 0,
        map.getCenter()?.lat() ?? 0,
      );

      const visible: number[] = [];
      const pixels: Array<google.maps.Point | null> = [];

      const hide = (i: number) => {
        this.nodes[i].style.display = "none";
        this.lines[i].style.display = "none";
      };

      this.points.forEach((point, i) => {
        if (bounds && !bounds.contains(point)) {
          pixels.push(null);
          hide(i);
          return;
        }
        const pixel = projection.fromLatLngToDivPixel(point);
        pixels.push(pixel);
        if (pixel) visible.push(i);
        else hide(i);
      });

      /**
       * Who is sitting on whom, at *this* zoom — bucketed, not compared.
       *
       * The obvious loop asks every pin about every group, which is fine for a
       * route's three stops and quadratic for a company's three thousand.
       * Buckets of one cluster-radius make it a nine-cell lookup each: the first
       * pin into a cell owns the group, so a group's centre never drifts as the
       * others join it.
       */
      const groups: Array<{ x: number; y: number; members: number[] }> = [];
      const cells = new Map<string, number[]>();
      const cellOf = (value: number) => Math.floor(value / CLUSTER_PX);

      for (const i of visible) {
        const pixel = pixels[i]!;
        const cx = cellOf(pixel.x);
        const cy = cellOf(pixel.y);

        let found = -1;
        for (let dx = -1; dx <= 1 && found < 0; dx += 1) {
          for (let dy = -1; dy <= 1 && found < 0; dy += 1) {
            for (const g of cells.get(`${cx + dx}:${cy + dy}`) ?? []) {
              const group = groups[g];
              if (
                Math.hypot(group.x - pixel.x, group.y - pixel.y) < CLUSTER_PX
              ) {
                found = g;
                break;
              }
            }
          }
        }

        if (found >= 0) {
          groups[found].members.push(i);
          continue;
        }

        const index = groups.length;
        groups.push({ x: pixel.x, y: pixel.y, members: [i] });
        const key = `${cx}:${cy}`;
        const bucket = cells.get(key);
        if (bucket) bucket.push(index);
        else cells.set(key, [index]);
      }

      this.discPool = 0;

      /*
       * What is already taken, in screen pixels — bucketed for the same reason
       * the grouping is. Asking every label about every rectangle already placed
       * is fine for a dozen pins and quadratic for a board's worth, and this
       * runs on every pan and every zoom rather than once.
       *
       * Pins go in first and labels are fitted against them, so a label never
       * covers another container's pin: losing a pin is losing a container, and
       * losing a label only costs a hover.
       */
      const RESERVE_PX = 64;
      const reserved = new Map<string, Array<[number, number, number, number]>>();

      const cellsFor = (x1: number, y1: number, x2: number, y2: number) => {
        const keys: string[] = [];
        for (let cx = Math.floor(x1 / RESERVE_PX); cx <= Math.floor(x2 / RESERVE_PX); cx += 1)
          for (let cy = Math.floor(y1 / RESERVE_PX); cy <= Math.floor(y2 / RESERVE_PX); cy += 1)
            keys.push(`${cx}:${cy}`);
        return keys;
      };

      const free = (x1: number, y1: number, x2: number, y2: number) =>
        cellsFor(x1, y1, x2, y2).every((key) =>
          (reserved.get(key) ?? []).every(
            ([a1, b1, a2, b2]) => !(x1 < a2 && x2 > a1 && y1 < b2 && y2 > b1),
          ),
        );

      const reserve = ([x1, y1, x2, y2]: [number, number, number, number]) => {
        for (const key of cellsFor(x1, y1, x2, y2)) {
          const bucket = reserved.get(key);
          if (bucket) bucket.push([x1, y1, x2, y2]);
          else reserved.set(key, [[x1, y1, x2, y2]]);
        }
      };

      const singles: Array<{
        i: number;
        x: number;
        y: number;
        side: "left" | "right";
      }> = [];

      for (const group of groups) {
        const count = group.members.length;

        if (count === 1) {
          const i = group.members[0];
          this.place(i, group.x, group.y, 0, 0, "right");
          reserve(this.pinBox(group.x, group.y));
          singles.push({ i, x: group.x, y: group.y, side: "right" });
          continue;
        }

        /*
         * How far this crowd really spreads, on the ground.
         *
         * Members join a group by landing within `CLUSTER_PX` of its anchor, so
         * the spread is bounded in pixels — but what it is worth in metres
         * depends entirely on the zoom, and that is what decides whether these
         * are one place or several.
         */
        const spreadM =
          Math.max(
            ...group.members.map((i) =>
              Math.hypot(pixels[i]!.x - group.x, pixels[i]!.y - group.y),
            ),
          ) * metresPerPixel;

        /*
         * How wide this fan would have to be.
         *
         * Worked out before deciding whether to draw one at all, because the
         * radius is half of that decision: a fan is only honest while it is
         * worth little on the ground, and how much it is worth depends on how
         * wide it is (see `FAN_GROUND_M`).
         *
         * Containers collected from *the same yard* have identical coordinates,
         * so zooming will never separate them — the gap has to be a distance on
         * the screen rather than on the ground, and a distance on the screen
         * changes meaning every time the zoom does. Which is why this is
         * computed here, on every draw, and not baked into the data.
         *
         * Big enough that neighbours clear each other and no bigger: a fan that
         * flings pins across the map stops reading as "these are all here".
         */
        const radius = Math.max(
          34,
          // Far enough apart that the teardrops themselves clear each other.
          SPACING_PX / 2 / Math.sin(Math.PI / count),
          /*
           * And far enough that the *labels* do — which is the term that
           * actually decides whether the fan is readable.
           *
           * Two pins one step apart on the ring are closest *vertically* at the
           * top and bottom of it, where the circle is flat: a step of
           * `Δ = 2π / count` from the top separates them by
           * `radius · (1 − cos Δ)`. Wanting that to be a label's height gives
           * the radius below, and it grows fast — five pins need 49px, eight
           * need 116px — because a circle only has so much height to share out
           * however wide you make it.
           *
           * Averaging it (`count · LABEL_ROW / 4`) was the first attempt and it
           * was wrong at exactly five, which is how it got noticed: five pins,
           * one name, and four labels dropped by the fitting pass for landing on
           * each other. The flat part of the circle is where the crowding is,
           * and an average does not see it.
           */
          LABEL_ROW / (1 - Math.cos((2 * Math.PI) / count)),
        );

        /*
         * A crowd stays a crowd when fanning it would not be true.
         *
         * Three ways to fail, and they are different failures. Too many, and
         * the ring is wider than the town it describes and its labels collide
         * with each other anyway. Too far zoomed out, and the ring is wider
         * than a county — pins in the wrong country, with a leader line as the
         * only clue. And not actually one place, in which case the fan is
         * asserting something false whatever size it is drawn at.
         *
         * Every one of them lands on the disc, which is the honest drawing in
         * all three cases: it says how many and what mix, it sits at the crowd
         * rather than inventing positions for its members, and clicking it
         * lists every one of them in the panel.
         */
        if (
          count > FAN_MAX ||
          spreadM > SAME_PLACE_M ||
          radius * metresPerPixel > FAN_GROUND_M
        ) {
          group.members.forEach(hide);
          this.placeDisc(group.x, group.y, group.members);
          reserve([group.x - 21, group.y - 21, group.x + 21, group.y + 21]);
          continue;
        }

        group.members.forEach((i, position) => {
          // From the top, clockwise — with two containers that is one above the
          // point and one below, which is the common case and the clearest.
          const angle = -Math.PI / 2 + (2 * Math.PI * position) / count;
          const dx = Math.cos(angle) * radius;
          const dy = Math.sin(angle) * radius;

          /*
           * Labels face outwards, away from the point they all share.
           *
           * A pin on the left of the ring wearing its label to the right draws
           * that label straight across the middle of the fan, over the other
           * pins and over their labels — so the left half of a fan would always
           * lose. Facing outwards gives the two halves their own column and
           * keeps the middle clear for the leader lines.
           */
          const side = dx < -1 ? "left" : "right";

          this.place(i, group.x, group.y, dx, dy, side);
          reserve(this.pinBox(group.x + dx, group.y + dy));
          singles.push({ i, x: group.x + dx, y: group.y + dy, side });
        });
      }

      // Labels last, and selected ones first among themselves — the container
      // somebody just clicked always gets to keep its name.
      singles.sort((a, b) => {
        const left = this.selected.has(this.pins[a.i].id) ? 0 : 1;
        const right = this.selected.has(this.pins[b.i].id) ? 0 : 1;
        return left - right;
      });

      for (const { i, x, y, side } of singles) {
        const width = this.labelWidths[i] || 0;
        const height = this.labelHeights[i] || 0;
        // Where the label actually sits: off the pin's shoulder — whichever
        // shoulder it was given — level with the teardrop's head rather than
        // with the point on the ground.
        const x1 =
          side === "right"
            ? x - PIN_W / 2 + LABEL_GAP
            : x + PIN_W / 2 - LABEL_GAP - width;
        const cy = y - PIN_H + LABEL_MID;
        const box: [number, number, number, number] = [
          x1,
          cy - height / 2,
          x1 + width,
          cy + height / 2,
        ];

        const fits = width > 0 && free(...box);
        this.nodes[i].dataset.labelled = fits ? "1" : "0";
        this.labels[i].style.display = fits ? "" : "none";
        if (fits) reserve(box);
      }

      // Discs left over from a denser draw are parked, not destroyed.
      for (let i = this.discPool; i < this.discs.length; i += 1) {
        this.discs[i].style.display = "none";
      }
    }

    /** The box a teardrop occupies — above its point, not around it. */
    private pinBox(x: number, y: number): [number, number, number, number] {
      return [x - PIN_W / 2, y - PIN_H, x + PIN_W / 2, y];
    }

    /** One pin, at its point plus however far it had to move to be seen. */
    private place(
      i: number,
      x: number,
      y: number,
      dx: number,
      dy: number,
      side: "left" | "right",
    ) {
      const node = this.nodes[i];
      node.style.display = "";
      node.style.left = `${x + dx}px`;
      node.style.top = `${y + dy}px`;

      /*
       * Which shoulder the label hangs from, set every draw.
       *
       * Every draw and not once, because a pin's side is a property of the fan
       * it is in at this zoom, not of the container: the same pin is on the left
       * of a fan at one zoom, alone with its label on the right at the next.
       * Both edges are written each time — leaving the old one set would pin the
       * label to both sides and stretch it.
       */
      const label = this.labels[i];
      if (side === "right") {
        label.style.left = `${LABEL_GAP}px`;
        label.style.right = "auto";
        label.style.alignItems = "flex-start";
      } else {
        label.style.right = `${LABEL_GAP}px`;
        label.style.left = "auto";
        // Ragged-left, so the two lines line up against the edge nearest the
        // pin rather than trailing away from it.
        label.style.alignItems = "flex-end";
      }

      const picked = this.selected.has(this.pins[i].id);
      node.style.zIndex = picked ? "8000" : String(10 + i);
      // A chosen pin is drawn bigger rather than recoloured: the colour is
      // already carrying the container's state, and a selection that overwrote
      // it would hide the thing the map is for.
      node.style.transform = picked
        ? "translate(-50%,-100%) scale(1.35)"
        : "translate(-50%,-100%)";

      const line = this.lines[i];
      if (!dx && !dy) {
        line.style.display = "none";
        return;
      }

      // A moved pin says where it really belongs, or it is just a pin in the
      // wrong place. The leader runs to the tip, which is the part that points.
      line.style.display = "";
      line.setAttribute("x1", String(x + dx));
      line.setAttribute("y1", String(y + dy));
      line.setAttribute("x2", String(x));
      line.setAttribute("y2", String(y));
    }

    /**
     * One crowd, as a disc that says how many are under it and what they are.
     *
     * The rim is the group's actual composition, in the proportions it has: a
     * ring that is mostly ink is mostly planned, and a wedge of copper in it is
     * exactly the work still to give away. Painting it the single most urgent
     * state — which it used to be — made a yard of eight planned containers and
     * one unplanned one read as nine waiting boxes, and hid every assigned
     * container on a busy board.
     */
    private placeDisc(x: number, y: number, members: number[]) {
      let disc = this.discs[this.discPool];
      let inner = this.discInners[this.discPool];

      if (!disc) {
        disc = document.createElement("div");
        disc.style.cssText = [
          "position:absolute;transform:translate(-50%,-50%)",
          "display:flex;align-items:center;justify-content:center",
          "width:42px;height:42px;border-radius:9999px",
          "border:2px solid #FFFFFF",
          "box-shadow:0 1px 5px rgba(11,15,14,0.35)",
          "cursor:pointer;z-index:5000",
        ].join(";");

        inner = document.createElement("span");
        inner.style.cssText = [
          "display:flex;align-items:center;justify-content:center",
          "width:30px;height:30px;border-radius:9999px",
          "background:#FFFFFF",
          "font-family:inherit;font-weight:800;font-size:12px;color:#0B0F0E",
        ].join(";");
        disc.appendChild(inner);

        this.root.appendChild(disc);
        this.discs.push(disc);
        this.discInners.push(inner);
      }

      this.discPool += 1;

      disc.style.display = "";
      disc.style.left = `${x}px`;
      disc.style.top = `${y}px`;
      inner.textContent = String(members.length);

      /*
       * The ring, in the order the legend reads: waiting, then planned, then
       * sent. Fixed order rather than "biggest first" so two neighbouring discs
       * can be compared at a glance — a wedge that moves around the dial
       * depending on its own size is a wedge nobody can compare.
       */
      const tally: Record<PinTone, number> = { waiting: 0, planned: 0, sent: 0 };
      for (const i of members) tally[this.pins[i].tone] += 1;

      const order = ["waiting", "planned", "sent"] as const;
      const stops: string[] = [];
      let sweep = 0;
      for (const tone of order) {
        if (!tally[tone]) continue;
        const next = sweep + (tally[tone] / members.length) * 360;
        stops.push(`${TONE_COLOR[tone]} ${sweep}deg ${next}deg`);
        sweep = next;
      }
      // A crowd of one state draws as that state, with no seam in it.
      disc.style.background =
        stops.length === 1
          ? TONE_COLOR[order.find((tone) => tally[tone])!]
          : `conic-gradient(${stops.join(",")})`;

      disc.style.outline = members.some((i) =>
        this.selected.has(this.pins[i].id),
      )
        ? "3px solid rgba(201,122,60,0.5)"
        : "";

      const ids = members.map((i) => this.pins[i].id);
      disc.onclick = (event) => {
        event.stopPropagation();
        this.options.onSelect(ids);
      };

      // The first few numbers, so hovering a disc is worth doing — the panel
      // lists all of them, and a tooltip the height of the screen helps nobody.
      const names = members.slice(0, 6).map((i) => this.pins[i].label);
      disc.title =
        members.length > names.length
          ? `${names.join(", ")}, …`
          : names.join(", ");
    }

    onRemove() {
      this.root.remove();
      this.nodes = [];
      this.labels = [];
      this.lines = [];
      this.discs = [];
      this.discInners = [];
      this.points = [];
    }
  };

  return MegaPins;
};

/**
 * Routes already fetched, by container id.
 *
 * Module-level, so flicking between two pins — or closing the map and opening
 * it again — costs nothing the second time. Emptied on an explicit reload (see
 * `load`), which is the one moment a dispatcher has asked to be told the truth
 * rather than shown what we remember: a route is edited on the container's own
 * form, so nothing the board does can invalidate it and no signal arrives to
 * say it has changed.
 */
const routeCache = new Map<string, PlanningMapStop[]>();

const API_KEY = import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_API_KEY || "";

/** A map of one town must not open at street level. */
const MAX_FIT_ZOOM = 14;

interface PlanningMegaMapProps {
  /** The window the placed half is drawn for — the board's own days. */
  start: string;
  end: string;
  /**
   * Moving that window, with the board's own controls.
   *
   * The handlers belong to the page and are lent here rather than duplicated,
   * so stepping a week forward on the map steps the grid behind it: the map is
   * another way of reading the same window, not a second window that has to be
   * reconciled with the first. Closing it leaves you on the week you navigated
   * to, which is the only behaviour that does not lose somebody's place.
   */
  mode: "week" | "day";
  onMode: (mode: "week" | "day") => void;
  onStep: (direction: 1 | -1) => void;
  onToday: () => void;
  /** Truck name by vehicle id, for saying which truck already has a container. */
  plates: Map<string, string>;
  /**
   * Put this container's number into the pool's search, and go there.
   *
   * ── Why there is no "pick up" here ──────────────────────────────────────────
   *
   * There was one, and it could not be made honest. The map deliberately reaches
   * past the board — every waiting container, against the board's capped page —
   * so a good share of what it draws has no card on the board to pick up, and a
   * button that works on some pins and not others is worse than no button.
   * Minting a card from the map's own lean row would be worse still: the grid
   * draws cards, a half-built one would be a half-drawn container, and placing
   * it would be a write against a thing nobody had checked.
   *
   * So the map hands over rather than acting. The pool's search runs
   * server-side and reaches the whole pile, which makes "put this number in it"
   * the one move that always works — and the container arrives in the sidebar
   * where the board's own pick-up, drag and drop already live.
   *
   * Offered only for containers that are actually in the pool. Searching it for
   * a container that already holds a slot would find nothing, because holding a
   * slot is precisely what takes a container out of the pool.
   */
  onFindInPool: (code: string) => void;
  onOpen: (container: PlanningMapContainer) => void;
  onClose: () => void;
}

export function PlanningMegaMap({
  start,
  end,
  mode,
  onMode,
  onStep,
  onToday,
  plates,
  onFindInPool,
  onOpen,
  onClose,
}: PlanningMegaMapProps) {
  const { t, i18n } = useTranslation(["calendar", "containerOrders"]);
  const hostRef = useRef<HTMLDivElement>(null);

  const [containers, setContainers] = useState<PlanningMapContainer[]>([]);
  const [unlocated, setUnlocated] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  /** The containers whose pin or disc was last clicked. */
  const [selected, setSelected] = useState<string[]>([]);
  /** Which states are drawn — the legend is the filter. */
  const [tones, setTones] = useState<Record<PinTone, boolean>>({
    waiting: true,
    planned: true,
    sent: true,
  });

  /**
   * The sidebar's filters, over the map.
   *
   * The same four questions the pool asks, because they are the same four
   * questions: which box, empty or full, whose line, and is it already under
   * way. Answered here entirely client-side — the map's payload carries every
   * waiting container precisely so that narrowing it is free, and a round trip
   * to hide the empties would give that back.
   *
   * Not shared with the sidebar's own filter state, deliberately. A dispatcher
   * narrowing the map to one carrier is asking a question about the map, and
   * coming back to a pool silently filtered the same way — which they would
   * then have to notice and undo — is a surprise nobody asked for.
   */
  const [search, setSearch] = useState("");
  const [loadFilter, setLoadFilter] = useState<"all" | "empty" | "full">(
    "all",
  );
  const [line, setLine] = useState<string | null>(null);
  const [hideStarted, setHideStarted] = useState(false);

  /**
   * Only the most recent load may write to state.
   *
   * The map refetches when the window changes and when a colleague moves
   * something, and those overlap on a busy desk — without the guard the slowest
   * answer wins and the map sits one change out of date until something else
   * happens to it.
   */
  const seq = useRef(0);

  const load = useCallback(
    async (quiet = false) => {
      const mine = ++seq.current;
      if (!quiet) {
        setLoading(true);
        // An explicit reload is the one moment somebody has asked to be told
        // the truth rather than shown what we remember.
        routeCache.clear();
        setRoutes(new Map());
      }
      try {
        const res = await planningAPI.getMap({ start, end });
        if (mine !== seq.current) return;
        setContainers(res.data.data.containers);
        setUnlocated(res.data.data.unlocated);
        setError(null);
      } catch (err) {
        if (mine !== seq.current) return;
        setError(
          (err as { response?: { data?: { message?: string } } }).response?.data
            ?.message ?? t("planning.errors.load"),
        );
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    },
    [start, end, t],
  );

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Somebody else moved something.
   *
   * Quiet and coalesced, exactly as the board handles the same signal: one
   * action can be several signals, and a spinner every time a colleague drags a
   * card would make the map unusable on a shared desk.
   */
  const pending = useRef<number | null>(null);
  useSocketEvent("planning:changed", () => {
    if (pending.current) window.clearTimeout(pending.current);
    pending.current = window.setTimeout(() => {
      pending.current = null;
      void load(true);
    }, 400);
  });

  useEffect(
    () => () => {
      if (pending.current) window.clearTimeout(pending.current);
    },
    [],
  );

  /**
   * The runs behind the pins the panel is showing.
   *
   * Fetched on selection rather than with the map, because the map draws
   * thousands of containers and the panel shows the one that was clicked — see
   * `getMapRoutes`. One request per selection, however many pins that is.
   */
  const [routes, setRoutes] = useState(() => new Map(routeCache));

  useEffect(() => {
    const missing = selected.filter((id) => !routeCache.has(id));
    if (!missing.length) return;

    let cancelled = false;
    planningAPI
      .getMapRoutes(missing)
      .then((res) => {
        for (const route of res.data.data.routes) {
          routeCache.set(route.id, route.stops);
        }
        /*
         * Anything that did not come back is remembered as having no route, or
         * the panel asks again on every render — a container that 404s would
         * otherwise fetch itself in a loop for as long as it stayed selected.
         */
        for (const id of missing) {
          if (!routeCache.has(id)) routeCache.set(id, []);
        }
        if (!cancelled) setRoutes(new Map(routeCache));
      })
      .catch(() => {
        // The card simply shows no route. It is extra detail on a panel that
        // already answers the question the pin was clicked for.
      });

    return () => {
      cancelled = true;
    };
  }, [selected]);

  /**
   * A container's slots, grouped by the day they fall on.
   *
   * `Fri, 18 Sep - slots 2, 3, 4` rather than the day repeated once per slot,
   * which is what a flat list of cells produces and what it looked like:
   * "Fri, Sep 18 · slot 2 · Fri, Sep 18 · slot 3 · Fri, Sep 18 · slot 4" says
   * one new thing per three read. The cells arrive earliest-first from the
   * server, so a Map preserves both the order of the days and the order within
   * them without sorting anything again.
   */
  const cellsByDay = useCallback((container: PlanningMapContainer) => {
    const days = new Map<string, number[]>();
    for (const cell of container.cells) {
      const slots = days.get(cell.date);
      if (slots) slots.push(cell.slot);
      else days.set(cell.date, [cell.slot]);
    }
    return [...days.entries()];
  }, []);

  /** The same thing on one line, for a tooltip. */
  const cellsSummary = useCallback(
    (container: PlanningMapContainer) =>
      cellsByDay(container)
        .map(
          ([date, slots]) =>
            `${new Date(`${date}T00:00:00`).toLocaleDateString(i18n.language, {
              weekday: "short",
            })} ${slots.join("/")}`,
        )
        .join(", "),
    [cellsByDay, i18n.language],
  );

  /**
   * Which days are on screen — the same sentence the board's toolbar shows.
   *
   * The header has to say it because the pins no longer can: a label reading
   * "Fri/2" told you the weekday and left you to work out which Friday, and now
   * it does not even say that. One window, stated once, at the top.
   */
  const windowLabel = useMemo(() => {
    const day = (value: string, withYear = false) =>
      new Date(`${value}T00:00:00`).toLocaleDateString(i18n.language, {
        day: "numeric",
        month: "short",
        ...(withYear ? { year: "numeric" } : {}),
      });

    // One day is named in full — there is room, and "Fri, 18 Sep – Fri, 18 Sep"
    // is a range that is not a range.
    return start === end
      ? new Date(`${start}T00:00:00`).toLocaleDateString(i18n.language, {
          weekday: "long",
          day: "numeric",
          month: "long",
        })
      : `${day(start)} – ${day(end, true)}`;
  }, [start, end, i18n.language]);

  const byId = useMemo(
    () => new Map(containers.map((container) => [container.id, container])),
    [containers],
  );

  /**
   * The containers the filters let through — everything except the legend.
   *
   * The legend sits on top of this rather than inside it, so the counts it
   * shows are counts of what the *other* filters left: switching to one carrier
   * and reading "waiting 4" means four of that carrier's, which is the only
   * reading that does not mislead.
   */
  const matching = useMemo(() => {
    const term = search.trim().toLowerCase();

    return containers.filter((container) => {
      if (loadFilter !== "all" && container.loadStatus !== loadFilter)
        return false;
      // `null` is every carrier; `""` is the containers with none named.
      if (line !== null && (container.shippingLine ?? "") !== line) return false;
      if (hideStarted && container.started) return false;
      if (!term) return true;

      return [
        container.code,
        container.containerNumber,
        container.client,
        container.city,
        container.shippingLine,
      ].some((value) => value?.toLowerCase().includes(term));
    });
  }, [containers, search, loadFilter, line, hideStarted]);

  /** How many of those wear each colour. */
  const counts = useMemo(() => {
    const tally: Record<PinTone, number> = { waiting: 0, planned: 0, sent: 0 };
    for (const container of matching) tally[toneOf(container)] += 1;
    return tally;
  }, [matching]);

  /** The carriers actually present, for the picker. */
  const shippingLines = useMemo(() => {
    const names = new Set<string>();
    let unnamed = false;
    for (const container of containers) {
      if (container.shippingLine) names.add(container.shippingLine);
      else unnamed = true;
    }
    return { names: [...names].sort((a, b) => a.localeCompare(b)), unnamed };
  }, [containers]);

  const filtered =
    Boolean(search.trim()) ||
    loadFilter !== "all" ||
    line !== null ||
    hideStarted;

  const pins = useMemo<MegaPin[]>(
    () =>
      matching.flatMap((container) => {
        const tone = toneOf(container);
        if (!tones[tone]) return [];

        const plate = container.vehicleId
          ? plates.get(container.vehicleId)
          : null;
        const where = [container.client, container.city]
          .filter(Boolean)
          .join(", ");
        // Only when it adds something: on a container whose client loads at its
        // own premises the two are the same string.
        const booking =
          container.orderClient && container.orderClient !== container.client
            ? container.orderClient
            : null;

        const code = container.code ?? "—";

        return [
          {
            id: container.id,
            lat: container.lat,
            lng: container.lng,
            label: code,
            plate: plate ?? null,
            slots: container.cells.length,
            tone,
            /*
             * The tooltip has the room the label has not — the box's own
             * number, the client, and the slots the pin only counts. Separated
             * like the label, so the two read as one voice.
             */
            title: [
              code,
              container.containerNumber,
              plate,
              where || null,
              booking,
              container.cells.length ? cellsSummary(container) : null,
            ]
              .filter(Boolean)
              .join(" · "),
          },
        ];
      }),
    [matching, tones, plates, cellsSummary],
  );

  /**
   * The drawn set as one string — an equal fingerprint is an equal map.
   *
   * The overlay is rebuilt only when the *pins* change, not when the parent
   * re-renders: a colleague's drag refetches this map, and throwing away the
   * zoom the dispatcher had chosen several times a minute would make it
   * unusable.
   */
  const fingerprint = useMemo(
    () =>
      pins
        .map(
          (pin) =>
            `${pin.id}:${pin.tone}:${pin.plate ?? ""}:${pin.slots}:${pin.lat.toFixed(5)},${pin.lng.toFixed(5)}`,
        )
        .join(";"),
    [pins],
  );

  const mapRef = useRef<google.maps.Map | null>(null);
  const overlayRef = useRef<MegaPinsHandle | null>(null);
  const pinsRef = useRef(pins);
  pinsRef.current = pins;

  /** Put everything drawn back on screen — the "I am lost" button. */
  const fitAll = useRef<() => void>(() => {});
  /** Whether the opening fit has already happened — see `frame` below. */
  const framed = useRef(false);

  useEffect(() => {
    if (!API_KEY || !pinsRef.current.length) return;

    let cancelled = false;
    const teardown: Array<() => void> = [];

    (async () => {
      try {
        await Promise.all(["core", "maps"].map(loadGoogleMapsLibrary));
        if (cancelled || !hostRef.current) return;

        const maps: typeof google.maps = window.google.maps;
        const drawn = pinsRef.current;

        const map =
          mapRef.current ??
          new maps.Map(hostRef.current, {
            center: { lat: drawn[0].lat, lng: drawn[0].lng },
            zoom: 8,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            clickableIcons: false,
            // The map is the whole dialog here, so it takes the wheel — there
            // is no page behind it that a scroll could be meant for.
            gestureHandling: "greedy",
            zoomControl: true,
            zoomControlOptions: { position: maps.ControlPosition.LEFT_TOP },
          });
        mapRef.current = map;
        setReady(true);

        const bounds = new maps.LatLngBounds();
        drawn.forEach((pin) => bounds.extend({ lat: pin.lat, lng: pin.lng }));

        const frame = () => {
          if (drawn.length === 1) {
            map.setCenter({ lat: drawn[0].lat, lng: drawn[0].lng });
            map.setZoom(12);
            return;
          }
          map.fitBounds(bounds, 64);
          const settle = maps.event.addListenerOnce(map, "idle", () => {
            if ((map.getZoom() ?? 0) > MAX_FIT_ZOOM) map.setZoom(MAX_FIT_ZOOM);
          });
          teardown.push(() => settle.remove());
        };
        fitAll.current = frame;

        const overlay = new (megaPinsClass(maps))(drawn, {
          onSelect: (ids) => setSelected(ids),
        });
        overlay.setMap(map);
        overlayRef.current = overlay;
        teardown.push(() => {
          overlay.setMap(null);
          overlayRef.current = null;
        });

        // Clicking the map itself is "never mind" — the same gesture the board
        // uses to put a held card down.
        const clear = map.addListener("click", () => setSelected([]));
        teardown.push(() => clear.remove());

        /*
         * Framed once, on the first map that is built, and never again.
         *
         * The overlay is rebuilt whenever the drawn set changes — a colleague
         * sending a container repaints one pin green — and re-fitting there
         * would throw away the zoom the dispatcher had chosen, mid-read, for a
         * change they did not make. So the view is theirs from the first frame
         * onwards, and "Fit all" is how they ask for it back.
         */
        if (!framed.current) {
          framed.current = true;
          frame();
        }
      } catch (err) {
        console.error("Planning map:", (err as Error)?.message);
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      teardown.forEach((off) => off());
    };
    // Rebuilt on the drawn set, never on a parent render — see `fingerprint`.
    // Everything else the effect reads it reads through a ref, which is what
    // keeps that promise honest rather than merely asserted.
  }, [fingerprint]);

  /** The map outlives each overlay; it dies with the dialog. */
  useEffect(
    () => () => {
      mapRef.current = null;
    },
    [],
  );

  useEffect(() => {
    overlayRef.current?.highlight(selected);
  }, [selected, fingerprint]);

  /** Escape closes, as it does everywhere else on this screen. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  /**
   * What was clicked, most urgent first.
   *
   * Sorted rather than left in whatever order the pins happened to be drawn,
   * because a crowd is the case where order is the whole value: eleven cards
   * that all look alike are eleven cards a dispatcher has to read to the bottom
   * of, and "which of these is owed first" is the question they were going to
   * ask anyway. Undated containers sort last — `￿` is past every digit — which
   * is the same key the pool's own deadline sort uses, so the two screens agree
   * about what "next" means.
   */
  const chosen = useMemo(() => {
    const key = (container: PlanningMapContainer) =>
      container.deadline
        ? `${container.deadline.day} ${container.deadline.time ?? "99:99"}`
        : "￿";

    return selected
      .map((id) => byId.get(id))
      .filter((container): container is PlanningMapContainer =>
        Boolean(container),
      )
      .sort((a, b) => key(a).localeCompare(key(b)));
  }, [selected, byId]);

  /**
   * The loading site the whole selection shares, or null when they differ.
   *
   * A crowd is a crowd *because* its containers are collected from one place —
   * that is what put them on one point — so the place is the one fact every
   * card in the list would otherwise repeat. Said once at the top, each card
   * gets to spend its space on what makes it different.
   *
   * Null for a selection of one: there is nothing to hoist out of a single
   * card, and a header restating the card below it is the same repetition in a
   * smaller quantity.
   */
  const sharedPlace = useMemo(() => {
    if (chosen.length < 2) return null;
    const first = chosen[0];
    if (!first.client && !first.city) return null;
    const same = chosen.every(
      (container) =>
        container.client === first.client && container.city === first.city,
    );
    if (!same) return null;
    return [first.client, first.city].filter(Boolean).join(", ");
  }, [chosen]);

  /** Which cards have had their route opened out. */
  const [openRoutes, setOpenRoutes] = useState<Set<string>>(new Set());

  /**
   * What kind of stop this is — the same naming the container's own page uses,
   * so one stop is not called two things on two screens.
   */
  const stopKind = (stop: PlanningMapStop) =>
    stop.role === "custom"
      ? t(`containerOrders:route.stopKinds.${stop.kind ?? "other"}`)
      : t(`containerOrders:route.${stop.role}`);

  const deadlineOf = (container: PlanningMapContainer) => {
    if (!container.deadline) return t("planning.pool.noDeadline");
    const day = new Date(
      `${container.deadline.day}T00:00:00`,
    ).toLocaleDateString(i18n.language, { day: "numeric", month: "short" });
    return container.deadline.time
      ? `${day} · ${container.deadline.time}`
      : day;
  };

  const drawable = pins.length;
  const quiet = API_KEY && !failed && !loading && !error;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* ── Which week, and how to move it ────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-3 py-2 lg:gap-3 lg:px-5">
        <span className="text-[13.5px] font-semibold text-dark-900">
          {t("planning.map.title")}
        </span>

        {/*
          The board's own navigation, lent to the map.
          
          Same controls, same handlers, same state — so stepping a week here
          steps the grid behind, and closing the map leaves the dispatcher on
          the week they navigated to rather than the one they opened from.
        */}
        <div className="flex shrink-0 overflow-hidden rounded-lg border border-gray-200">
          {(["week", "day"] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => onMode(value)}
              className={`px-3 py-1 text-[12px] font-semibold transition ${
                mode === value
                  ? "bg-dark-900 text-white"
                  : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              {t(`planning.mode.${value}`)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onStep(-1)}
            className="rounded-md border border-gray-200 p-1.5 text-gray-500 transition hover:bg-gray-50"
            aria-label={t("planning.nav.previous")}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onToday}
            className="rounded-md border border-gray-200 px-2.5 py-1.5 text-[12px] font-semibold text-gray-600 transition hover:bg-gray-50"
          >
            {t("planning.nav.today")}
          </button>
          <button
            type="button"
            onClick={() => onStep(1)}
            className="rounded-md border border-gray-200 p-1.5 text-gray-500 transition hover:bg-gray-50"
            aria-label={t("planning.nav.next")}
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </div>

        <span className="text-[12.5px] font-semibold text-dark-900">
          {windowLabel}
        </span>

        <button
          type="button"
          onClick={() => fitAll.current()}
          className="ml-auto flex items-center gap-1.5 rounded-md border border-gray-200 px-2.5 py-1.5 text-[12px] font-semibold text-gray-600 transition hover:bg-gray-50"
        >
          <Crosshair className="h-3.5 w-3.5" />
          {t("planning.map.fitAll")}
        </button>

        <button
          type="button"
          onClick={() => void load()}
          className="rounded-md border border-gray-200 p-1.5 text-gray-500 transition hover:bg-gray-50"
          aria-label={t("planning.nav.refresh")}
        >
          <RotateCcw className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-gray-200 p-1.5 text-gray-500 transition hover:bg-gray-50"
          aria-label={t("planning.map.close")}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* ── What is on the map, and what is being left off it ─────────────── */}
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-3 py-2 lg:gap-3 lg:px-5">
        <span className="text-[12px] text-gray-500">
          {t("planning.map.shown", { count: drawable })}
          {/*
            Containers the company has that this map cannot draw, counted by the
            server because the client never receives them. A map silently
            showing nine hundred of a thousand is one a week gets planned
            against — and the fix, an address picked from Places on the
            container's own form, is something a dispatcher can actually do.
          */}
          {unlocated > 0 && (
            <>
              {" · "}
              <span className="text-amber-700">
                {t("planning.map.unlocated", { count: unlocated })}
              </span>
            </>
          )}
        </span>

        {/* The legend *is* the filter — a colour you can switch off is a colour
            you have understood. */}
        <div className="flex flex-wrap items-center gap-1.5">
          {(["waiting", "planned", "sent"] as const).map((tone) => (
            <button
              key={tone}
              type="button"
              onClick={() =>
                setTones((current) => ({ ...current, [tone]: !current[tone] }))
              }
              className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] font-semibold transition ${
                tones[tone]
                  ? "border-gray-300 bg-white text-gray-700"
                  : "border-gray-200 bg-gray-50 text-gray-400"
              }`}
              aria-pressed={tones[tone]}
            >
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{
                  background: tones[tone] ? TONE_COLOR[tone] : "#D1D5DB",
                }}
              />
              {t(`planning.map.tone.${tone}`)}
              <span className="text-gray-400">{counts[tone]}</span>
            </button>
          ))}
        </div>

        {/* ── The sidebar's questions, asked of the map ─────────────────── */}
        <div className="relative ml-auto w-full sm:w-[190px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("planning.map.searchPlaceholder")}
            className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-2 text-[12px] focus:border-secondary-500 focus:outline-none focus:ring-1 focus:ring-secondary-500"
          />
        </div>

        <div className="flex shrink-0 overflow-hidden rounded-md border border-gray-200">
          {(["all", "empty", "full"] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setLoadFilter(value)}
              className={`px-2.5 py-1.5 text-[11.5px] font-semibold transition ${
                loadFilter === value
                  ? "bg-dark-900 text-white"
                  : "bg-white text-gray-500 hover:bg-gray-50"
              }`}
            >
              {value === "all"
                ? t("planning.pool.filters.allLoads")
                : t(`containerOrders:loadStatus.${value}`)}
            </button>
          ))}
        </div>

        <select
          value={line ?? "__all"}
          onChange={(event) =>
            setLine(event.target.value === "__all" ? null : event.target.value)
          }
          className="shrink-0 rounded-md border border-gray-200 px-2 py-1.5 text-[11.5px] font-semibold text-gray-600 focus:border-secondary-500 focus:outline-none"
        >
          <option value="__all">{t("planning.pool.filters.allLines")}</option>
          {shippingLines.names.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
          {shippingLines.unnamed && (
            <option value="">{t("planning.pool.filters.noLine")}</option>
          )}
        </select>

        <button
          type="button"
          onClick={() => setHideStarted((value) => !value)}
          className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-[11.5px] font-semibold transition ${
            hideStarted
              ? "border-dark-900 bg-dark-900 text-white"
              : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
          }`}
        >
          {t("planning.pool.filters.hideStarted")}
        </button>

        {/* One press back to the whole picture — a filtered map that looks
            empty should never be mistaken for an empty week. */}
        {filtered && (
          <button
            type="button"
            onClick={() => {
              setSearch("");
              setLoadFilter("all");
              setLine(null);
              setHideStarted(false);
            }}
            className="shrink-0 rounded-md px-2 py-1.5 text-[11.5px] font-semibold text-secondary-700 transition hover:bg-gray-50"
          >
            {t("planning.pool.filters.clear")}
          </button>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col sm:flex-row">
        <div className="relative min-w-0 flex-1 bg-bone">
          <div ref={hostRef} className="h-full w-full" />

          {(!API_KEY || failed) && (
            <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-[13px] text-mist">
              {t("planning.map.unavailable")}
            </div>
          )}

          {API_KEY && !failed && loading && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2 bg-white/75 text-[13px] text-mist">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("planning.map.loading")}
            </div>
          )}

          {API_KEY && !failed && !loading && error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white/90 px-6 text-center">
              <p className="text-[13px] text-red-600">{error}</p>
              <button
                type="button"
                onClick={() => void load()}
                className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-gray-600"
              >
                {t("planning.nav.refresh")}
              </button>
            </div>
          )}

          {quiet && !drawable && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/75 px-6 text-center text-[13px] text-mist">
              {t("planning.map.empty")}
            </div>
          )}

          {quiet && Boolean(drawable) && !ready && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2 text-[13px] text-mist">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("planning.map.loading")}
            </div>
          )}
        </div>

        {/*
          What was clicked, as a list.

          A list rather than an info window, because the common click is a crowd
          off one client's yard — and six info windows, or one that pages, is a
          worse reading of the same fact. It is also where the map stops being a
          picture: a container is picked up from here and placed on the board
          behind it.
        */}
        {chosen.length > 0 && (
          <aside
            /* Below `sm` there is no room beside the map, so the list becomes a
               sheet under it — the map stays the bigger half, because a list
               that covered it would answer the question by hiding it. */
            className="flex max-h-[45%] w-full shrink-0 flex-col border-t border-gray-200 sm:max-h-none sm:w-[300px] sm:border-l sm:border-t-0"
          >
            <div className="flex items-start gap-2 border-b border-gray-200 px-3 py-2">
              {/*
                One container needs no headcount — the card under it already
                says which one, and "1 container here" is a label that only
                repeats the obvious. A crowd does need one, because clicking a
                disc is the one gesture whose answer is a list whose length is
                not otherwise visible.
              */}
              <span className="min-w-0 flex-1">
                <span className="block text-[12.5px] font-semibold text-dark-900">
                  {chosen.length > 1
                    ? t("planning.map.selected", { count: chosen.length })
                    : t("planning.map.selectedOne")}
                </span>
                {/* The place they all share, said once instead of eleven
                    times — see `sharedPlace`. */}
                {sharedPlace && (
                  <span className="block truncate text-[11px] text-gray-500">
                    {sharedPlace}
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() => setSelected([])}
                className="shrink-0 rounded-md p-1 text-gray-400 transition hover:bg-gray-50"
                aria-label={t("planning.map.clearSelection")}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-auto p-2">
              {chosen.map((container) => {
                const tone = toneOf(container);
                const plate = container.vehicleId
                  ? plates.get(container.vehicleId)
                  : null;
                const stops = routes.get(container.id);

                return (
                  <div
                    key={container.id}
                    className="mb-2 rounded-lg border border-gray-200 bg-white p-2.5"
                  >
                    <div className="flex items-center gap-1.5">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ background: TONE_COLOR[tone] }}
                      />
                      <span className="truncate text-[12.5px] font-bold text-dark-900">
                        {container.code ?? "—"}
                      </span>
                      {/* The box's own number, where one has been allocated —
                          off the pin, but this panel has room for it. */}
                      {container.containerNumber && (
                        <span className="truncate font-mono text-[10px] text-gray-500">
                          {container.containerNumber}
                        </span>
                      )}
                    </div>

                    {/* Dropped when the header already said it for the whole
                        selection — repeating it per card is what made a crowd
                        of eleven unreadable. */}
                    {!sharedPlace && (
                      <>
                        <p className="mt-1 truncate text-[11.5px] text-gray-600">
                          {container.client ?? t("planning.card.noCity")}
                        </p>
                        {container.city && (
                          <p className="truncate text-[11px] text-gray-400">
                            {container.city}
                          </p>
                        )}
                      </>
                    )}

                    {/*
                      Whose booking it is — the lines above are the loading site.

                      Never suppressed for matching the line above it. The client
                      is a Partner and the loading site is an Addresses record:
                      two directories, two kinds of thing, and two records that
                      happen to share a company name are a coincidence rather
                      than a duplicate. Hiding one on a string match blanked the
                      client exactly when both facts were real.

                      Labelled for the same reason — without it a second bare
                      company name under an address is unreadable.
                    */}
                    <p className="truncate text-[10.5px] text-gray-500">
                      <span className="font-bold uppercase tracking-wider text-gray-400">
                        {t("planning.card.client")}
                      </span>{" "}
                      {container.orderClient || EMPTY}
                    </p>

                    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10.5px] text-gray-500">
                      <span>{deadlineOf(container)}</span>
                      {/*
                        The client's own appointment, beside the deadline rather
                        than instead of it.

                        They answer different questions and are routinely
                        different days: the deadline is whatever stop comes first
                        (often a terminal), this is the time the client is
                        expecting a truck at the loading site. Two pins forty
                        minutes apart are one trip or two depending entirely on
                        this, which is the decision the map exists for.
                      */}
                      <span className="flex items-center gap-1 font-semibold text-secondary-700">
                        <CalendarClock className="h-3 w-3" />
                        {/*
                          Date and time each stand on their own — see
                          `onSiteSlot`. Neither is ever shown in the other's
                          place: a date where a time should be is the one error
                          somebody would plan a morning around.
                        */}
                        {onSiteSlot(container)}
                      </span>
                      {plate && (
                        <span className="flex items-center gap-1 font-semibold text-gray-600">
                          <Truck className="h-3 w-3" />
                          {plate}
                        </span>
                      )}
                    </div>

                    {/*
                      Every slot it holds — one line per day, the slots of that
                      day listed together. This is the whole answer the pin's
                      badge is a count of.
                    */}
                    {container.cells.length > 0 && (
                      <ul className="mt-1 space-y-0.5">
                        {cellsByDay(container).map(([date, slots]) => (
                          <li
                            key={date}
                            className="text-[10.5px] font-semibold text-gray-600"
                          >
                            {t("planning.map.daySlots", {
                              count: slots.length,
                              day: new Date(
                                `${date}T00:00:00`,
                              ).toLocaleDateString(i18n.language, {
                                weekday: "short",
                                day: "numeric",
                                month: "short",
                              }),
                              slots: slots.join(", "),
                            })}
                          </li>
                        ))}
                      </ul>
                    )}

                    {/*
                      Where this container actually goes — start to finish.
                      Stops marked "Not used" never appear: the container is not
                      going there, so they are not part of the run.
                    */}
                    {stops === undefined ? (
                      <p className="mt-2 border-t border-gray-100 pt-1.5 text-[10.5px] text-gray-400">
                        {t("planning.map.routeLoading")}
                      </p>
                    ) : stops.length > 0 && !openRoutes.has(container.id) ? (
                      /*
                        The run in one line, which is what a crowd needs.
                        
                        Eleven containers clustered on one point are clustered
                        *because* they share a loading site, and in practice they
                        share terminals too — so the full addressed route was
                        repeated eleven times identically, taking more space than
                        everything that actually told them apart. Names only,
                        folded to one line, and the addresses are one press away
                        for the container that turns out to matter.
                      */
                      <button
                        type="button"
                        onClick={() =>
                          setOpenRoutes((open) =>
                            new Set(open).add(container.id),
                          )
                        }
                        className="mt-2 flex w-full items-center gap-1 border-t border-gray-100 pt-1.5 text-left text-[10.5px] text-gray-500 transition hover:text-dark-900"
                      >
                        <ChevronRight className="h-3 w-3 shrink-0 text-gray-400" />
                        <span className="min-w-0 flex-1 truncate">
                          {stops
                            .map((stop) => stop.name ?? stopKind(stop))
                            .join(" → ")}
                        </span>
                      </button>
                    ) : stops && stops.length > 0 ? (
                      <ol className="mt-2 space-y-1 border-t border-gray-100 pt-1.5">
                        {stops.map((stop, index) => {
                          const kind = stopKind(stop);
                          // Where it is, as much of it as was filled in.
                          const address = [stop.street, stop.city]
                            .filter(Boolean)
                            .join(", ");

                          return (
                            <li
                              key={`${container.id}:${index}`}
                              className="flex items-start gap-1.5 text-[10.5px] leading-tight"
                            >
                              <span className="mt-px shrink-0 font-mono font-bold text-dark-900">
                                {index + 1}.
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-gray-700">
                                  {stop.name ?? kind}
                                </span>
                                {/* What kind of stop, then where it is. The
                                    name is truncated because a company name
                                    reads from its front; the address is allowed
                                    to wrap, because a street cut off halfway is
                                    an address nobody can drive to. */}
                                {stop.name && (
                                  <span className="block truncate text-gray-400">
                                    {kind}
                                  </span>
                                )}
                                {address && (
                                  <span className="block text-gray-400">
                                    {address}
                                  </span>
                                )}
                              </span>
                            </li>
                          );
                        })}
                        <li>
                          <button
                            type="button"
                            onClick={() =>
                              setOpenRoutes((open) => {
                                const next = new Set(open);
                                next.delete(container.id);
                                return next;
                              })
                            }
                            className="flex items-center gap-1 text-[10.5px] text-gray-400 transition hover:text-dark-900"
                          >
                            <ChevronDown className="h-3 w-3" />
                            {t("planning.map.routeCollapse")}
                          </button>
                        </li>
                      </ol>
                    ) : null}

                    <div className="mt-2 flex gap-1.5">
                      {/*
                        Offered only for a container that is genuinely in the
                        pool. Searching the pool for one that already holds a
                        slot would find nothing — holding a slot is exactly what
                        takes a container out of the pool — so the button would
                        be a promise the sidebar could not keep.
                      */}
                      {!container.cells.length && container.code && (
                        <button
                          type="button"
                          onClick={() => onFindInPool(container.code ?? "")}
                          className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-dark-900 px-2 py-1.5 text-[11px] font-semibold text-dark-900 transition hover:bg-gray-50"
                        >
                          <Search className="h-3 w-3" />
                          {t("planning.map.findInPool")}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => onOpen(container)}
                        className={`rounded-md border border-gray-200 px-2 py-1.5 text-[11px] font-semibold text-gray-600 transition hover:bg-gray-50 ${
                          container.cells.length || !container.code
                            ? "flex-1"
                            : ""
                        }`}
                      >
                        {t("planning.card.open")}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
